import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Calculator, CheckCircle2, IndianRupee } from "lucide-react";
import { useApi, useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import employeeJobWorkApi from "../api/employeeJobWorkApi";
import contractorLedgerApi from "../api/contractorLedgerApi";
import hundekariLedgerApi from "../api/hundekariLedgerApi";
import transportLedgerApi from "../api/transportLedgerApi";
import vendorPaymentApi from "../api/vendorPaymentApi";
import expensePaymentApi from "../api/expensePaymentApi";
import expenseMasterApi from "../api/expenseMasterApi";
import jobWorkEntryApi from "../api/jobWorkEntryApi";
import vendorApi from "../api/vendorApi";
import customerApi from "../api/customerApi";
import invoiceApi from "../api/invoiceApi";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Select2 from "../components/Select2";
import PageHeader from "../components/PageHeader";
import AdvanceOutstandingChips from "../components/AdvanceOutstandingChips";
import T from "../theme/colors";

const money = v => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = d => d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
const num = v => Number(v || 0);

const PAID_MODES = ["CASH", "BANK", "UPI", "CHEQUE", "OTHER"];
const RECEIPT_MODES = ["CASH", "CHEQUE", "NEFT", "RTGS", "UPI", "OTHER"];
/** Short human line for a failed outstanding fetch - silence hides real problems. */
const describeErr = (what, err) => {
    const st = err?.response?.status;
    const msg = err?.response?.data?.message || err?.message || "";
    if (st === 403) return `No permission to load ${what} (403) — check the module grants.`;
    if (st) return `Could not load ${what} (HTTP ${st}${msg ? ` — ${msg}` : ""}).`;
    return `Could not load ${what}${msg ? ` — ${msg}` : ""}.`;
};

const ADVANCE_TYPES = ["ADVANCE", "PF", "ESI", "PT", "MACHINERY", "PURCHASE", "LOAN"];

/* Shared little building blocks ─────────────────────────────────────────── */


function SectionTitle({ children, right }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "14px 0 6px" }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2 }}>{children}</span>
            {right}
        </div>
    );
}

/**
 * Payment Hub — one page for every payment the office records.
 *
 * ADVANCE: money given ahead of work (staff / contractor), typed by bucket.
 * PAID: pick the type and the party, and the party's OUTSTANDING items load
 *   — vendor bills, unpaid job work entries, unpaid salary slips, or the
 *   running ledger balance. Tick items or Auto-Allocate and the amount
 *   fills itself; whatever is not assigned to an item stays ON ACCOUNT.
 * RECEIPT: pick the CUSTOMER (Scholar), their unpaid invoices load, the
 *   amount auto-allocates oldest-first across them.
 *
 * Every save goes through the type's OWN endpoint - the hub maps, it never
 * invents a second bookkeeping path. Everything lands in the All Payments
 * Register.
 */
export default function PaymentHubPage() {
    const { hasPermission } = useAuth();
    const [tab, setTab] = useState("PAID");

    const [log, setLog] = useState([]);
    const pushLog = entry => setLog(p => [{ ...entry, at: new Date() }, ...p].slice(0, 20));

    const canStaff = hasPermission("EMPLOYEE_LEDGER", "CREATE");
    const canContractor = hasPermission("CONTRACTOR_LEDGER", "CREATE");
    const canReceipt = hasPermission("INVOICE_REGISTER", "CREATE");
    const canAdvance = canStaff || canContractor;

    const TabBtn = ({ id, icon, children }) => (
        <button type="button" onClick={() => setTab(id)} style={{
            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px",
            borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700,
            border: `1px solid ${tab === id ? "var(--primary)" : T.border}`,
            background: tab === id ? "var(--primary)" : T.bgCard,
            color: tab === id ? "#fff" : T.text1, transition: "all 0.15s",
        }}>{icon}{children}</button>
    );

    return (
        <div className="fade-up" style={{ padding: "20px 24px" }}>
            <PageHeader
                title="Payment Entry"
                subtitle="Advances, payments and customer receipts — pick the party, the outstanding loads, the numbers work themselves out."
                breadcrumb={["Finance", "Payment Entry"]}
            />

            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                {canAdvance && <TabBtn id="ADVANCE" icon={<IndianRupee size={15} />}>Advance</TabBtn>}
                <TabBtn id="PAID" icon={<ArrowUp size={15} />}>Payment Paid</TabBtn>
                {canReceipt && <TabBtn id="RECEIPT" icon={<ArrowDown size={15} />}>Payment Receipt</TabBtn>}
            </div>

            {tab === "ADVANCE" && canAdvance && <AdvanceTab canStaff={canStaff} canContractor={canContractor} onDone={pushLog} />}
            {tab === "PAID" && <PaidTab onDone={pushLog} />}
            {tab === "RECEIPT" && canReceipt && <ReceiptTab onDone={pushLog} />}

            {log.length > 0 && <SessionLog log={log} />}
        </div>
    );
}

/* ── ADVANCE ───────────────────────────────────────────────────────────── */

function AdvanceTab({ canStaff, canContractor, onDone }) {
    const { mutate } = useMutation();
    const [advanceType, setAdvanceType] = useState("ADVANCE");

    const { data: empS } = useApi(employeeJobWorkApi.getSummary, [], { cacheKey: "hubEmp" });
    const { data: conS } = useApi(contractorLedgerApi.getSummary, [], { cacheKey: "hubCon" });
    const arr = v => (Array.isArray(v) ? v : []);

    // ONE dropdown for everyone an advance can go to. The type rides inside
    // the value ("STAFF:12"), so picking the person picks the flow - the
    // operator just searches a name, never chooses a category first.
    const partyOptions = useMemo(() => [
        ...(canStaff ? arr(empS).map(e => ({
            value: `STAFF:${e.employeeId}`,
            label: `${e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ""} · Staff`,
            // The summaries carry `outstanding` (credit - debit); `balance`
            // never existed on them, so the hint below never showed.
            bal: e.outstanding ?? e.balance,
        })) : []),
        ...(canContractor ? arr(conS).map(c => ({
            value: `CONTRACTOR:${c.contractorId}`,
            label: `${c.contractorName}${c.contractorCode ? ` (${c.contractorCode})` : ""} · Contractor`,
            bal: c.outstanding ?? c.balance,
        })) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [empS, conS, canStaff, canContractor]);

    const EMPTY = { party: "", date: today(), amount: "", paymentMode: "CASH", reference: "", notes: "" };
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const f = k => v => setForm(p => ({ ...p, [k]: v }));
    const selected = partyOptions.find(o => o.value === form.party);
    const [pType, pId] = (form.party || "").split(":");
    // Bumped after each save so the outstanding chips re-read the ledger.
    const [savedCount, setSavedCount] = useState(0);

    const save = async () => {
        const errs = {};
        if (!form.party) errs.party = "Party is required";
        if (!form.date) errs.date = "Date is required";
        if (!(num(form.amount) > 0)) errs.amount = "Amount must be above zero";
        if (Object.keys(errs).length) { setErrors(errs); return; }
        const base = {
            advanceType, amount: num(form.amount), paymentDate: form.date,
            paymentMode: form.paymentMode || undefined,
            paymentReference: form.reference.trim() || undefined,
            notes: form.notes.trim() || undefined,
        };
        await mutate(
            () => pType === "STAFF"
                ? employeeJobWorkApi.recordAdvance({ ...base, employeeId: Number(pId) })
                : contractorLedgerApi.recordAdvance({ ...base, contractorId: Number(pId) }),
            {
                successMsg: `${advanceType} recorded`,
                onSuccess: () => {
                    onDone({ dir: "OUT", type: `Advance · ${advanceType}`, party: selected?.label || "", amount: num(form.amount) });
                    setForm(p => ({ ...p, amount: "", reference: "", notes: "" })); setErrors({});
                    setSavedCount(n => n + 1);
                },
            }
        );
    };

    return (
        <Card>
            <div style={{ padding: 16, maxWidth: 720 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                    <div>
                        <Select2 label="Party *" value={form.party} onChange={f("party")}
                            placeholder="Search staff or contractor…" options={partyOptions} error={errors.party} />
                        {selected && selected.bal != null && (
                            // Ledger balance is credit - debit: below 0 he owes us.
                            <div style={{ fontSize: 12, marginTop: 4, color: num(selected.bal) < 0 ? "var(--danger)" : T.text2 }}>
                                Ledger balance: <b>{money(Math.abs(num(selected.bal)))}</b>
                                {num(selected.bal) < 0 ? " (owes us)" : num(selected.bal) > 0 ? " (we owe)" : ""}
                            </div>
                        )}
                        {selected && (
                            <AdvanceOutstandingChips
                                partyType={pType === "STAFF" ? "EMPLOYEE" : "CONTRACTOR"}
                                partyId={pId} highlight={advanceType} refreshKey={savedCount} />
                        )}
                    </div>
                    <Input label="Date *" type="date" value={form.date} onChange={f("date")} error={errors.date} />
                </div>

                <SectionTitle>Advance Type</SectionTitle>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {ADVANCE_TYPES.map(a => (
                        <button key={a} type="button" onClick={() => setAdvanceType(a)} style={{
                            padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700,
                            border: `2px solid ${advanceType === a ? "#F59E0B" : T.border}`,
                            background: advanceType === a ? "#FEF3C7" : T.bgCard,
                            color: advanceType === a ? "#92400E" : T.text1,
                        }}>{a === "LOAN" ? "Loan / Baki" : a}</button>
                    ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
                    <Input label="Amount ₹ *" type="number" value={form.amount} onChange={f("amount")} placeholder="0.00" error={errors.amount} />
                    <Select2 label="Mode" value={form.paymentMode} onChange={f("paymentMode")}
                        options={PAID_MODES.map(m => ({ value: m, label: m }))} />
                    <Input label="Reference No" value={form.reference} onChange={f("reference")} placeholder="Cheque / UPI ref" />
                </div>
                <div style={{ marginTop: 12 }}>
                    <Input label="Notes" value={form.notes} onChange={f("notes")} placeholder="Optional" />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                    <Btn onClick={save}>Record Advance</Btn>
                </div>
            </div>
        </Card>
    );
}

/* ── PAYMENT PAID ──────────────────────────────────────────────────────── */

function PaidTab({ onDone }) {
    const { hasPermission } = useAuth();
    const { mutate } = useMutation();

    const canStaff = hasPermission("EMPLOYEE_LEDGER", "CREATE");
    const canContractor = hasPermission("CONTRACTOR_LEDGER", "CREATE");
    const canHundekari = hasPermission("HUNDEKARI_LEDGER", "CREATE");
    const canTransport = hasPermission("TRANSPORT_LEDGER", "CREATE");
    const canVendor = hasPermission("VENDOR_PAYMENT", "CREATE");
    const canExpense = hasPermission("EXPENSE_PAYMENT", "CREATE");
    const anyType = canStaff || canContractor || canHundekari || canTransport || canVendor || canExpense;

    const { data: empS } = useApi(employeeJobWorkApi.getSummary, [], { cacheKey: "hubEmp" });
    const { data: conS } = useApi(contractorLedgerApi.getSummary, [], { cacheKey: "hubCon" });
    const { data: hunS } = useApi(hundekariLedgerApi.getSummary, [], { cacheKey: "hubHun" });
    const { data: traS } = useApi(transportLedgerApi.getSummary, [], { cacheKey: "hubTra" });
    const { data: venS } = useApi(vendorApi.getAll, [], { cacheKey: "hubVen" });
    const { data: expS } = useApi(expenseMasterApi.getActive, [], { cacheKey: "hubExp" });
    const arr = v => (Array.isArray(v) ? v : []);

    // ONE dropdown for everyone money can go to - the type rides inside the
    // value ("VENDOR:5") and on the label, so search spans every kind at once.
    const partyOptions = useMemo(() => [
        ...(canStaff ? arr(empS).map(e => ({
            value: `STAFF:${e.employeeId}`,
            label: `${e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ""}${e.isTeamLeader ? " — Team Leader" : ""} · Staff`,
            bal: e.balance,
        })) : []),
        ...(canContractor ? arr(conS).map(c => ({ value: `CONTRACTOR:${c.contractorId}`, label: `${c.contractorName} · Contractor`, bal: c.balance })) : []),
        ...(canHundekari ? arr(hunS).map(h => ({ value: `HUNDEKARI:${h.hundekariId}`, label: `${h.hundekariName} · Hundekari`, bal: h.balance })) : []),
        ...(canTransport ? arr(traS).map(x => ({ value: `TRANSPORT:${x.transportId}`, label: `${x.transportName} · Transport`, bal: x.balance })) : []),
        ...(canVendor ? arr(venS).map(v => ({ value: `VENDOR:${v.id}`, label: `${v.vendorName} · Vendor` })) : []),
        ...(canExpense ? arr(expS).map(x => ({ value: `EXPENSE:${x.id}`, label: `${x.expenseName} · Expense` })) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [empS, conS, hunS, traS, venS, expS, canStaff, canContractor, canHundekari, canTransport, canVendor, canExpense]);

    const EMPTY = { party: "", date: today(), amount: "", paymentMode: "CASH", reference: "", paidTo: "", bankAccount: "", notes: "" };
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const f = k => v => setForm(p => ({ ...p, [k]: v }));
    const selected = partyOptions.find(o => o.value === form.party);
    const [type, pidStr] = (form.party || "").split(":");
    const pid = Number(pidStr || 0);
    const TYPE_LABELS = { STAFF: "Staff", CONTRACTOR: "Contractor", HUNDEKARI: "Hundekari", TRANSPORT: "Transport", VENDOR: "Vendor", EXPENSE: "Expense" };
    const typeLabel = TYPE_LABELS[type] || "";

    // ── Outstanding items for the picked party ──
    const [bills, setBills] = useState([]);          // VENDOR
    const [alloc, setAlloc] = useState({});          // VENDOR: purchaseInvoiceId → amount
    const [jwEntries, setJwEntries] = useState([]);  // STAFF / CONTRACTOR
    const [jwAlloc, setJwAlloc] = useState({});      // entryId → amount (PARTIAL allowed)
    const [slips, setSlips] = useState([]);          // STAFF fixed salary
    const [slipAlloc, setSlipAlloc] = useState({});  // slipId → amount (PARTIAL allowed)
    const [loadingOut, setLoadingOut] = useState(false);
    const [outError, setOutError] = useState("");   // why the panel is empty, when it failed
    const [reloadKey, setReloadKey] = useState(0);

    const resetOutstanding = () => { setBills([]); setAlloc({}); setJwEntries([]); setJwAlloc({}); setSlips([]); setSlipAlloc({}); setOutError(""); };

    useEffect(() => {
        resetOutstanding();
        setErrors({});
        if (!form.party) return;
        let dead = false;
        setLoadingOut(true);
        const done = () => { if (!dead) setLoadingOut(false); };
        if (type === "VENDOR") {
            vendorPaymentApi.outstandingBills(pid)
                .then(r => { if (!dead) { const l = r?.data?.data ?? r?.data ?? []; setBills(Array.isArray(l) ? l : []); } })
                .catch(err => { if (!dead) { setBills([]); setOutError(describeErr("outstanding bills", err)); } }).finally(done);
        } else if (type === "STAFF" || type === "CONTRACTOR") {
            const calls = [
                jobWorkEntryApi.getUnpaid({
                    payeeType: type === "STAFF" ? "EMPLOYEE" : "CONTRACTOR",
                    employeeId: type === "STAFF" ? pid : undefined,
                    contractorId: type === "CONTRACTOR" ? pid : undefined,
                }).then(r => { if (!dead) { const l = r?.data?.data ?? r?.data ?? []; setJwEntries(Array.isArray(l) ? l : []); } }).catch(err => { if (!dead) { setJwEntries([]); setOutError(describeErr("job work entries", err)); } }),
            ];
            if (type === "STAFF") {
                calls.push(employeeJobWorkApi.getUnpaidSalarySlips(pid)
                    .then(r => { if (!dead) { const l = r?.data?.data ?? r?.data ?? []; setSlips(Array.isArray(l) ? l : []); } }).catch(err => { if (!dead) { setSlips([]); setOutError(describeErr("salary slips", err)); } }));
            }
            Promise.all(calls).finally(done);
        } else { setLoadingOut(false); }
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.party, reloadKey]);

    // ── Dues. paidAmount/dueAmount come from the backend now; older rows
    // without them fall back to the full net. ──
    const jwDue = e => e.dueAmount != null ? num(e.dueAmount)
        : num(e.netAmount != null ? e.netAmount : e.totalAmount) - num(e.paidAmount);
    const slipDue = s2 => s2.dueAmount != null ? num(s2.dueAmount) : num(s2.netAmount) - num(s2.paidAmount);

    const slipAllocated = slips.reduce((s, x) => s + num(slipAlloc[x.id]), 0);
    const jwAllocated = jwEntries.reduce((s, e) => s + num(jwAlloc[e.id]), 0);
    const staffAllocated = slipAllocated + jwAllocated;
    const staffOnAccount = num(form.amount) - staffAllocated;
    const staffLineOver = slips.some(x => num(slipAlloc[x.id]) > slipDue(x) + 1e-6)
        || jwEntries.some(e => num(jwAlloc[e.id]) > jwDue(e) + 1e-6)
        || slips.some(x => num(slipAlloc[x.id]) < 0) || jwEntries.some(e => num(jwAlloc[e.id]) < 0);

    // Auto-allocate: spread the amount oldest-first, slips before job work;
    // the last item that doesn't fully fit takes the PARTIAL remainder -
    // exactly the vendor-bill behaviour. Leftover past all dues = ON ACCOUNT.
    const staffAutoAllocate = () => {
        let remaining = num(form.amount);
        if (remaining <= 0) return;
        const nextSlips = {}, nextJw = {};
        for (const s2 of slips) {
            if (remaining <= 0) break;
            const give = Math.min(slipDue(s2), remaining);
            if (give > 0) { nextSlips[s2.id] = give.toFixed(2); remaining -= give; }
        }
        for (const e of jwEntries) {
            if (remaining <= 0) break;
            const give = Math.min(jwDue(e), remaining);
            if (give > 0) { nextJw[e.id] = give.toFixed(2); remaining -= give; }
        }
        setSlipAlloc(nextSlips); setJwAlloc(nextJw);
    };

    // Per-row "Full": the item's whole due, capped by what the amount still has room for.
    const fillFull = (kind, id, due) => {
        const amt = num(form.amount);
        const others = staffAllocated - num((kind === "SLIP" ? slipAlloc : jwAlloc)[id]);
        const room = amt > 0 ? Math.max(0, amt - others) : due;
        const give = amt > 0 ? Math.min(due, room) : due;
        const set = kind === "SLIP" ? setSlipAlloc : setJwAlloc;
        set(p => ({ ...p, [id]: give ? give.toFixed(2) : "" }));
    };

    // ── Vendor allocation math (same rules as the Vendor Payment page) ──
    const allocSummary = useMemo(() => {
        let allocated = 0, lineOver = false;
        for (const b of bills) {
            const v = num(alloc[b.purchaseInvoiceId]);
            if (v < 0 || v > num(b.outstanding) + 1e-6) lineOver = true;
            allocated += v;
        }
        const amt = num(form.amount);
        return { allocated, onAccount: amt - allocated, overAllocated: allocated > amt + 1e-6, lineOver };
    }, [alloc, bills, form.amount]);

    const vendorAutoAllocate = () => {
        let remaining = num(form.amount);
        if (remaining <= 0) return;
        const next = {};
        for (const b of bills) {
            if (remaining <= 0) break;
            const give = Math.min(num(b.outstanding), remaining);
            if (give > 0) { next[b.purchaseInvoiceId] = give.toFixed(2); remaining -= give; }
        }
        setAlloc(next);
    };

    const save = async () => {
        const errs = {};
        if (!form.party) errs.party = type === "EXPENSE" ? "Category is required" : "Party is required";
        if (!form.date) errs.date = "Date is required";
        if (!(num(form.amount) > 0)) errs.amount = "Amount must be above zero";
        if (type === "VENDOR") {
            if (allocSummary.lineOver) errs.alloc = "An allocation exceeds that bill's outstanding";
            if (allocSummary.overAllocated) errs.alloc = "Allocated total exceeds the payment amount";
        }
        if (type === "STAFF" || type === "CONTRACTOR") {
            if (staffLineOver) errs.alloc = "An allocation exceeds that item's remaining due";
            if (staffOnAccount < -0.009) errs.alloc = "Allocated total exceeds the amount — raise the amount or reduce a line";
        }
        if (Object.keys(errs).length) { setErrors(errs); return; }

        const amount = num(form.amount);
        const base = { paymentMode: form.paymentMode, paymentReference: form.reference.trim() || undefined, notes: form.notes.trim() || undefined };
        const slipLines = slips.map(x => ({ itemId: x.id, amount: num(slipAlloc[x.id]) })).filter(l => l.amount > 0);
        const jwLines = jwEntries.map(e => ({ itemId: e.id, amount: num(jwAlloc[e.id]) })).filter(l => l.amount > 0);

        const call = async () => {
            switch (type) {
                case "STAFF": {
                    // Slip allocations first, then job-work allocations, then the
                    // leftover ON ACCOUNT - up to three posts through the proper
                    // endpoints. Dues reduce by exactly what each line says; an item
                    // closes only when its due hits zero.
                    if (slipLines.length > 0) {
                        await employeeJobWorkApi.recordSalaryPayment({
                            employeeId: pid, slipAllocations: slipLines, paymentDate: form.date, ...base,
                        });
                    }
                    if (jwLines.length > 0) {
                        await employeeJobWorkApi.recordPaymentTds({
                            ...base, employeeId: pid, grossAmount: jwAllocated, paymentDate: form.date,
                            fromDate: "2020-01-01", toDate: today(), jobWorkAllocations: jwLines,
                        });
                    }
                    if (staffOnAccount > 0.009) {
                        await employeeJobWorkApi.recordPaymentTds({
                            ...base, employeeId: pid, grossAmount: staffOnAccount, paymentDate: form.date,
                            fromDate: "2020-01-01", toDate: today(),
                        });
                    }
                    if (slipLines.length === 0 && jwLines.length === 0 && staffOnAccount <= 0.009) throw new Error("Nothing to pay");
                    return { data: { ok: true } };
                }
                case "CONTRACTOR": {
                    if (jwLines.length > 0) {
                        await contractorLedgerApi.recordPaymentTds({
                            ...base, contractorId: pid, grossAmount: jwAllocated, paymentDate: form.date,
                            fromDate: "2020-01-01", toDate: today(), jobWorkAllocations: jwLines,
                        });
                    }
                    if (staffOnAccount > 0.009) {
                        await contractorLedgerApi.recordPaymentTds({
                            ...base, contractorId: pid, grossAmount: staffOnAccount, paymentDate: form.date,
                            fromDate: "2020-01-01", toDate: today(),
                        });
                    }
                    if (jwLines.length === 0 && staffOnAccount <= 0.009) throw new Error("Nothing to pay");
                    return { data: { ok: true } };
                }
                case "HUNDEKARI": return hundekariLedgerApi.recordPayment({
                    hundekariId: pid, amount, date: form.date, paymentMode: form.paymentMode,
                    paymentReference: form.reference.trim() || null, notes: form.notes.trim() || null,
                });
                case "TRANSPORT": return transportLedgerApi.recordPayment({
                    transportId: pid, amount, date: form.date, paymentMode: form.paymentMode,
                    paymentReference: form.reference.trim() || null, notes: form.notes.trim() || null,
                });
                case "VENDOR": {
                    const allocations = bills
                        .map(b => ({ purchaseInvoiceId: b.purchaseInvoiceId, amount: num(alloc[b.purchaseInvoiceId]) }))
                        .filter(a => a.amount > 0);
                    return vendorPaymentApi.create({
                        vendorId: pid, paymentDate: form.date, amount, paymentMode: form.paymentMode,
                        referenceNo: form.reference.trim() || null, bankAccount: form.bankAccount.trim() || null,
                        narration: form.notes.trim() || null, allocations: allocations.length ? allocations : null,
                    });
                }
                case "EXPENSE": return expensePaymentApi.create({
                    expenseMasterId: pid, paymentDate: form.date, amount, paymentMode: form.paymentMode,
                    referenceNo: form.reference.trim() || null, paidTo: form.paidTo.trim() || null,
                    narration: form.notes.trim() || null, status: "CONFIRMED",
                });
                default: return Promise.reject(new Error("Pick who to pay"));
            }
        };
        await mutate(call, {
            successMsg: `${typeLabel} payment recorded`,
            onSuccess: () => {
                onDone({ dir: "OUT", type: typeLabel, party: selected?.label || "", amount });
                setForm(p => ({ ...p, amount: "", reference: "", paidTo: "", notes: "" }));
                setErrors({});
                setReloadKey(k => k + 1);   // dues re-pull, settled items drop off
            },
        });
    };

    if (!anyType) return (
        <Card><div style={{ padding: 30, textAlign: "center", color: T.text2, fontSize: 13 }}>
            You don't have CREATE permission on any payment type.
        </div></Card>
    );

    const ledgerBalanceOnly = type === "HUNDEKARI" || type === "TRANSPORT";
    const hasPanel = !!form.party && (type === "VENDOR" || type === "STAFF" || type === "CONTRACTOR" || ledgerBalanceOnly);

    return (
        <Card>
            {/* Form LEFT, outstanding RIGHT — the long lists live in their own
          scrolling panel so Amount / Mode / Notes never sink below them. */}
            <div style={{ display: "grid", gridTemplateColumns: hasPanel ? "minmax(340px, 420px) 1fr" : "minmax(340px, 720px)", gap: 0, alignItems: "start" }}>

                {/* ── LEFT: the form ── */}
                <div style={{ padding: 16 }}>
                    <Select2 label="Pay To *" value={form.party} onChange={f("party")}
                        placeholder="Search staff, contractor, hundekari, transport, vendor, expense…"
                        options={partyOptions} error={errors.party} />
                    {selected && selected.bal != null && (
                        <div style={{ fontSize: 12, marginTop: 4, color: num(selected.bal) > 0 ? "var(--danger)" : T.text2 }}>
                            Outstanding balance: <b>{money(selected.bal)}</b>
                            {ledgerBalanceOnly && num(selected.bal) > 0 && (
                                <button type="button" onClick={() => f("amount")(num(selected.bal).toFixed(2))}
                                    style={{ marginLeft: 8, border: "none", background: "none", cursor: "pointer", color: "var(--primary)", fontSize: 12, fontWeight: 700, padding: 0 }}>
                                    Pay full
                                </button>
                            )}
                        </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        <Input label="Date *" type="date" value={form.date} onChange={f("date")} error={errors.date} />
                        <Input label="Amount ₹ *" type="number" value={form.amount} onChange={f("amount")} placeholder="0.00" error={errors.amount} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        <Select2 label="Mode *" value={form.paymentMode} onChange={f("paymentMode")}
                            options={PAID_MODES.map(m => ({ value: m, label: m }))} />
                        <Input label="Reference No" value={form.reference} onChange={f("reference")} placeholder="Cheque / UPI ref" />
                    </div>

                    {type === "EXPENSE" && (
                        <div style={{ marginTop: 12 }}>
                            <Input label="Paid To" value={form.paidTo} onChange={f("paidTo")} placeholder="Vendor / recipient name" />
                        </div>
                    )}
                    {type === "VENDOR" && (
                        <div style={{ marginTop: 12 }}>
                            <Input label="Bank Account" value={form.bankAccount} onChange={f("bankAccount")} placeholder="Optional — account paid from" />
                        </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <Input label="Notes" value={form.notes} onChange={f("notes")} placeholder="Why this payment was made" />
                    </div>

                    {/* Allocation roll-up lives beside the button, always visible. */}
                    {type === "VENDOR" && bills.length > 0 && (
                        <div style={{ fontSize: 12, marginTop: 12, color: allocSummary.overAllocated || allocSummary.lineOver ? "var(--danger)" : T.text2 }}>
                            Against bills <b>{money(allocSummary.allocated)}</b> · On account <b style={{ color: allocSummary.onAccount > 0 ? "var(--info)" : "inherit" }}>{money(Math.max(0, allocSummary.onAccount))}</b>
                            {errors.alloc && <div style={{ color: "var(--danger)", marginTop: 2 }}>{errors.alloc}</div>}
                        </div>
                    )}
                    {(type === "STAFF" || type === "CONTRACTOR") && (slips.length > 0 || jwEntries.length > 0) && (
                        <div style={{ fontSize: 12, marginTop: 12, color: errors.alloc ? "var(--danger)" : T.text2 }}>
                            Against items <b>{money(staffAllocated)}</b> · On account <b style={{ color: staffOnAccount > 0 ? "var(--info)" : "inherit" }}>{money(Math.max(0, staffOnAccount))}</b>
                            {errors.alloc && <div style={{ color: "var(--danger)", marginTop: 2 }}>{errors.alloc}</div>}
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                        <Btn onClick={save}>Record Payment</Btn>
                    </div>
                </div>

                {/* ── RIGHT: the party's outstanding, in its own scroll ── */}
                {hasPanel && (
                    <div style={{ borderLeft: `1px solid ${T.border}`, padding: 16, minWidth: 0 }}>
                        {loadingOut && <div style={{ padding: 16, color: T.text2, fontSize: 13 }}>Loading outstanding…</div>}
                        {!loadingOut && outError && (
                            <div style={{
                                padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "var(--danger)",
                                border: "1px solid var(--danger)", borderRadius: 8, background: "#FEF2F2"
                            }}>
                                {outError}
                            </div>
                        )}

                        {ledgerBalanceOnly && !loadingOut && (
                            <div style={{ border: `1px dashed ${T.border}`, borderRadius: 8, padding: 16, fontSize: 13, color: T.text1 }}>
                                {typeLabel} runs on a running ledger — the outstanding balance is shown under the name,
                                and every payment goes ON ACCOUNT against it.
                            </div>
                        )}

                        {type === "VENDOR" && !loadingOut && (
                            <>
                                <SectionTitle right={bills.length > 0 && (
                                    <span style={{ display: "inline-flex", gap: 8 }}>
                                        <Btn size="sm" variant="outline" icon={<Calculator size={12} />} onClick={vendorAutoAllocate}>Auto Allocate</Btn>
                                        <Btn size="sm" variant="outline" onClick={() => setAlloc({})}>Clear</Btn>
                                    </span>
                                )}>
                                    Outstanding Bills — oldest first
                                </SectionTitle>
                                {bills.length === 0 && (
                                    <div style={{ padding: 14, fontSize: 13, color: T.text2, border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                                        No outstanding bills — the whole amount goes ON ACCOUNT.
                                    </div>
                                )}
                                {bills.length > 0 && (
                                    <div style={{ fontSize: 12, margin: "0 0 8px", color: T.text1 }}>
                                        {bills.length} bill{bills.length > 1 ? "s" : ""} · Total due <b style={{ color: "var(--danger)" }}>{money(bills.reduce((t2, b2) => t2 + num(b2.outstanding), 0))}</b>
                                    </div>
                                )}
                                {bills.length > 0 && (
                                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                        <div style={{ maxHeight: 440, overflowY: "auto" }}>
                                            {bills.map((b, i) => (
                                                <AllocRow key={b.purchaseInvoiceId} first={i === 0}
                                                    no={b.ourInvoiceNumber || b.vendorInvoiceNumber || `Bill #${b.purchaseInvoiceId}`}
                                                    sub={fmtDate(b.invoiceDate)} due={num(b.outstanding)}
                                                    value={alloc[b.purchaseInvoiceId] || ""}
                                                    onChange={v => setAlloc(p => ({ ...p, [b.purchaseInvoiceId]: v }))}
                                                    onFull={() => {
                                                        const amt = num(form.amount);
                                                        const others = bills.reduce((s2, x) => x.purchaseInvoiceId === b.purchaseInvoiceId ? s2 : s2 + num(alloc[x.purchaseInvoiceId]), 0);
                                                        const room = amt > 0 ? Math.max(0, amt - others) : num(b.outstanding);
                                                        const give = amt > 0 ? Math.min(num(b.outstanding), room) : num(b.outstanding);
                                                        setAlloc(p => ({ ...p, [b.purchaseInvoiceId]: give ? give.toFixed(2) : "" }));
                                                    }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {(type === "STAFF" || type === "CONTRACTOR") && !loadingOut && (
                            <>
                                <SectionTitle right={(slips.length > 0 || jwEntries.length > 0) && (
                                    <span style={{ display: "inline-flex", gap: 8 }}>
                                        <Btn size="sm" variant="outline" icon={<Calculator size={12} />} onClick={staffAutoAllocate}>Auto Allocate</Btn>
                                        <Btn size="sm" variant="outline" onClick={() => { setSlipAlloc({}); setJwAlloc({}); }}>Clear</Btn>
                                    </span>
                                )}>
                                    Outstanding — oldest first
                                </SectionTitle>
                                {slips.length === 0 && jwEntries.length === 0 && (
                                    <div style={{ padding: 14, fontSize: 13, color: T.text2, border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                                        Nothing unpaid — the whole amount goes ON ACCOUNT against the ledger.
                                    </div>
                                )}
                                {(slips.length > 0 || jwEntries.length > 0) && (
                                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, margin: "0 0 8px", color: T.text1 }}>
                                        {slips.length > 0 && (
                                            <span>{slips.length} slip{slips.length > 1 ? "s" : ""} · <b style={{ color: "var(--info)" }}>{money(slips.reduce((t2, x) => t2 + slipDue(x), 0))}</b></span>
                                        )}
                                        {jwEntries.length > 0 && (
                                            <span>{jwEntries.length} JW · <b style={{ color: "var(--primary)" }}>{money(jwEntries.reduce((t2, x) => t2 + jwDue(x), 0))}</b></span>
                                        )}
                                        <span>Total due <b style={{ color: "var(--danger)" }}>{money(slips.reduce((t2, x) => t2 + slipDue(x), 0) + jwEntries.reduce((t2, x) => t2 + jwDue(x), 0))}</b></span>
                                    </div>
                                )}
                                {(slips.length > 0 || jwEntries.length > 0) && (
                                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                        <div style={{ maxHeight: 440, overflowY: "auto" }}>
                                            {slips.map((s2, i) => (
                                                <AllocRow key={`sl${s2.id}`} first={i === 0} tag="SALARY"
                                                    no={s2.entryNumber} who={s2.workerName} sub={`${fmtDate(s2.fromDate)} – ${fmtDate(s2.toDate)}`}
                                                    due={slipDue(s2)} paid={num(s2.paidAmount)}
                                                    value={slipAlloc[s2.id] || ""}
                                                    onChange={v => setSlipAlloc(p => ({ ...p, [s2.id]: v }))}
                                                    onFull={() => fillFull("SLIP", s2.id, slipDue(s2))} />
                                            ))}
                                            {jwEntries.map((e, i) => (
                                                <AllocRow key={`jw${e.id}`} first={slips.length === 0 && i === 0} tag="JOB WORK"
                                                    no={e.entryNumber || `#${e.id}`} who={e.employeeName} sub={fmtDate(e.entryDate)}
                                                    due={jwDue(e)} paid={num(e.paidAmount)}
                                                    value={jwAlloc[e.id] || ""}
                                                    onChange={v => setJwAlloc(p => ({ ...p, [e.id]: v }))}
                                                    onFull={() => fillFull("JW", e.id, jwDue(e))} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(slips.length > 0 || jwEntries.length > 0) && (
                                    <div style={{ fontSize: 12, color: T.text2, marginTop: 6 }}>
                                        Type any amount against any item — its due reduces by exactly that, and the item
                                        closes when the due hits zero. Auto Allocate spreads the amount oldest first (the
                                        last item takes the partial); anything beyond the items goes ON ACCOUNT. TDS
                                        applies proportionally, automatic.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
}

/* One outstanding row with a partial-amount box: number + due (+ paid-so-far) + Full + input. */
function AllocRow({ first, tag, no, sub, who, due, paid, value, onChange, onFull }) {
    return (
        <div style={{
            display: "grid", gridTemplateColumns: tag ? "auto minmax(0,1fr) auto auto 104px" : "minmax(0,1fr) auto auto 104px",
            gap: 8, alignItems: "center",
            padding: "8px 12px", borderTop: first ? "none" : `1px solid ${T.border}`, fontSize: 13,
        }}>
            {tag && (
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.04em", color: tag === "SALARY" ? "var(--info)" : "var(--primary)", whiteSpace: "nowrap" }}>{tag}</span>
            )}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)", fontSize: 12 }}>{no}</span>
                {who && <span style={{ fontWeight: 700, color: T.text0, fontSize: 12, marginLeft: 8 }}>{who}</span>}
                <span style={{ color: T.text2, fontSize: 12, marginLeft: 8 }}>{sub}</span>
            </span>
            <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <span style={{ color: T.text2 }}>Due {money(due)}</span>
                {paid > 0 && (
                    <span style={{ display: "block", fontSize: 10, color: "var(--info)", fontWeight: 700 }}>paid {money(paid)}</span>
                )}
            </span>
            <button type="button" onClick={onFull}
                style={{ border: "none", background: "none", cursor: "pointer", color: "var(--primary)", fontSize: 12, fontWeight: 700 }}>
                Full
            </button>
            <input type="number" value={value} placeholder="0.00"
                onChange={e => onChange(e.target.value)}
                style={{
                    padding: "6px 9px", borderRadius: 6, fontSize: 13, textAlign: "right",
                    border: `1px solid ${T.border}`, background: T.bgCard, color: T.text0, outline: "none", boxSizing: "border-box", width: "100%"
                }} />
        </div>
    );
}

/* ── PAYMENT RECEIPT — customer-first, Scholar only ────────────────────── */

function ReceiptTab({ onDone }) {
    const { mutate } = useMutation();
    const { data: cusS } = useApi(customerApi.getAll, [], { cacheKey: "hubCus" });
    const customers = Array.isArray(cusS) ? cusS : [];

    const [customerId, setCustomerId] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alloc, setAlloc] = useState({});   // invoiceId → amount

    const EMPTY = { date: today(), amount: "", paymentMode: "CASH", reference: "", notes: "" };
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const f = k => v => setForm(p => ({ ...p, [k]: v }));
    const selected = customers.find(c => String(c.id) === customerId);

    const loadInvoices = (cid) => {
        setInvoices([]); setAlloc({});
        if (!cid) return;
        setLoading(true);
        // Oldest first so auto-allocation clears the oldest dues first —
        // the same order a hand-kept khata would settle them.
        invoiceApi.pagedScholar({ partyId: Number(cid), page: 1, size: 100, sortBy: "invoiceDate", sortDir: "asc" })
            .then(r => {
                const body = r?.data?.data ?? r?.data ?? {};
                const list = Array.isArray(body.items) ? body.items : [];
                setInvoices(list.filter(x => x.paymentStatus !== "PAID" && num(x.balanceAmount) > 0));
            })
            .catch(() => setInvoices([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { loadInvoices(customerId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [customerId]);

    const totalDue = invoices.reduce((s, x) => s + num(x.balanceAmount), 0);
    const allocated = invoices.reduce((s, x) => s + num(alloc[x.id]), 0);
    const amt = num(form.amount);
    const unassigned = amt - allocated;

    const autoAllocate = () => {
        let remaining = num(form.amount);
        if (remaining <= 0) return;
        const next = {};
        for (const inv of invoices) {
            if (remaining <= 0) break;
            const give = Math.min(num(inv.balanceAmount), remaining);
            if (give > 0) { next[inv.id] = give.toFixed(2); remaining -= give; }
        }
        setAlloc(next);
    };

    const save = async () => {
        const errs = {};
        if (!customerId) errs.customerId = "Customer is required";
        if (!form.date) errs.date = "Date is required";
        if (!(amt > 0)) errs.amount = "Amount must be above zero";
        const lines = invoices
            .map(inv => ({ inv, amount: num(alloc[inv.id]) }))
            .filter(l => l.amount > 0);
        for (const l of lines) {
            if (l.amount > num(l.inv.balanceAmount) + 1e-6) errs.alloc = `Allocation on ${l.inv.invoiceNumber} exceeds its balance`;
        }
        if (!errs.alloc && lines.length === 0 && amt > 0) errs.alloc = "Allocate the amount — use Auto Allocate";
        // A customer receipt has no on-account bucket - every rupee must land
        // on an invoice, so an unassigned remainder blocks the save.
        if (!errs.alloc && Math.abs(unassigned) > 0.009) {
            errs.alloc = unassigned > 0
                ? `${money(unassigned)} is not assigned to any invoice — receipts have no on-account`
                : "Allocated total exceeds the amount";
        }
        if (Object.keys(errs).length) { setErrors(errs); return; }

        await mutate(
            async () => {
                // One InvoicePayment per allocated invoice, oldest first.
                for (const l of lines) {
                    await invoiceApi.recordPayment(l.inv.id, {
                        paymentDate: form.date, amount: l.amount, paymentMode: form.paymentMode,
                        referenceNo: form.reference.trim() || null, notes: form.notes.trim() || null,
                    });
                }
                return { data: { ok: true } };
            },
            {
                successMsg: `Receipt recorded across ${lines.length} invoice${lines.length > 1 ? "s" : ""}`,
                onSuccess: () => {
                    onDone({ dir: "IN", type: "Receipt", party: selected?.customerName || "", amount: amt });
                    setForm(p => ({ ...p, amount: "", reference: "", notes: "" }));
                    setErrors({}); loadInvoices(customerId);
                },
            }
        );
    };

    const hasPanel = !!customerId;

    return (
        <Card>
            {/* Form LEFT, invoices RIGHT — same shape as Payment Paid, so the
          amount and mode never sink below a long invoice list. */}
            <div style={{ display: "grid", gridTemplateColumns: hasPanel ? "minmax(340px, 420px) 1fr" : "minmax(340px, 720px)", gap: 0, alignItems: "start" }}>

                {/* ── LEFT: the form ── */}
                <div style={{ padding: 16 }}>
                    <Select2 label="Received From *" value={customerId} onChange={setCustomerId}
                        placeholder="Search customer…"
                        options={customers.map(c => ({ value: String(c.id), label: c.customerName }))} error={errors.customerId} />
                    {customerId && !loading && (
                        <div style={{ fontSize: 12, marginTop: 4, color: totalDue > 0 ? "var(--danger)" : "var(--success)" }}>
                            Total outstanding: <b>{money(totalDue)}</b> across {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
                        </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        <Input label="Date *" type="date" value={form.date} onChange={f("date")} error={errors.date} />
                        <Input label="Amount ₹ *" type="number" value={form.amount} onChange={f("amount")} placeholder="0.00" error={errors.amount} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        <Select2 label="Mode *" value={form.paymentMode} onChange={f("paymentMode")}
                            options={RECEIPT_MODES.map(m => ({ value: m, label: m }))} />
                        <Input label="Reference No" value={form.reference} onChange={f("reference")} placeholder="Cheque / UTR / txn id" />
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <Input label="Notes" value={form.notes} onChange={f("notes")} placeholder="Optional" />
                    </div>

                    {customerId && invoices.length > 0 && (
                        <div style={{ fontSize: 12, marginTop: 12, color: errors.alloc ? "var(--danger)" : T.text2 }}>
                            Allocated <b>{money(allocated)}</b>
                            {amt > 0 && Math.abs(unassigned) > 0.009 && <span> · Unassigned <b style={{ color: "var(--danger)" }}>{money(unassigned)}</b></span>}
                            {errors.alloc && <div style={{ color: "var(--danger)", marginTop: 2 }}>{errors.alloc}</div>}
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                        <Btn onClick={save}>Record Receipt</Btn>
                    </div>
                </div>

                {/* ── RIGHT: outstanding invoices, own scroll ── */}
                {hasPanel && (
                    <div style={{ borderLeft: `1px solid ${T.border}`, padding: 16, minWidth: 0 }}>
                        {loading && <div style={{ padding: 16, color: T.text2, fontSize: 13 }}>Loading invoices…</div>}
                        {!loading && (
                            <>
                                <SectionTitle right={invoices.length > 0 && (
                                    <span style={{ display: "inline-flex", gap: 8 }}>
                                        <Btn size="sm" variant="outline" icon={<Calculator size={12} />} onClick={autoAllocate}>Auto Allocate</Btn>
                                        <Btn size="sm" variant="outline" onClick={() => setAlloc({})}>Clear</Btn>
                                    </span>
                                )}>
                                    Outstanding Invoices — oldest first
                                </SectionTitle>
                                {invoices.length === 0 && (
                                    <div style={{ padding: 14, fontSize: 13, color: T.text2, border: `1px dashed ${T.border}`, borderRadius: 8 }}>
                                        Nothing outstanding — this customer is settled.
                                    </div>
                                )}
                                {invoices.length > 0 && (
                                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                                        <div style={{ maxHeight: 440, overflowY: "auto" }}>
                                            {invoices.map((inv, i) => (
                                                <div key={inv.id} style={{
                                                    display: "grid", gridTemplateColumns: "minmax(0,2fr) 1fr auto 104px", gap: 8, alignItems: "center",
                                                    padding: "8px 12px", borderTop: i === 0 ? "none" : `1px solid ${T.border}`, fontSize: 13,
                                                }}>
                                                    <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {inv.invoiceNumber}
                                                        <span style={{ color: T.text2, fontWeight: 400, marginLeft: 8, fontFamily: "inherit" }}>{fmtDate(inv.invoiceDate)}</span>
                                                    </span>
                                                    <span style={{ textAlign: "right", color: T.text2, whiteSpace: "nowrap" }}>Due {money(inv.balanceAmount)}</span>
                                                    <button type="button" onClick={() => {
                                                        const others = invoices.reduce((s2, x) => x.id === inv.id ? s2 : s2 + num(alloc[x.id]), 0);
                                                        const room = amt > 0 ? Math.max(0, amt - others) : num(inv.balanceAmount);
                                                        const give = amt > 0 ? Math.min(num(inv.balanceAmount), room) : num(inv.balanceAmount);
                                                        setAlloc(p => ({ ...p, [inv.id]: give ? give.toFixed(2) : "" }));
                                                    }} style={{ border: "none", background: "none", cursor: "pointer", color: "var(--primary)", fontSize: 12, fontWeight: 700 }}>
                                                        Full
                                                    </button>
                                                    <input type="number" value={alloc[inv.id] || ""} placeholder="0.00"
                                                        onChange={e => setAlloc(p => ({ ...p, [inv.id]: e.target.value }))}
                                                        style={{
                                                            padding: "6px 9px", borderRadius: 6, fontSize: 13, textAlign: "right",
                                                            border: `1px solid ${T.border}`, background: T.bgCard, color: T.text0, outline: "none", boxSizing: "border-box", width: "100%"
                                                        }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
}

/* ── Session log ───────────────────────────────────────────────────────── */

function SessionLog({ log }) {
    return (
        <Card style={{ marginTop: 14 }}>
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2 }}>
                Recorded this session — full history in the All Payments Register
            </div>
            {log.map((e, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
                    <CheckCircle2 size={14} color={e.dir === "IN" ? "var(--info)" : "var(--success)"} />
                    <span style={{ fontWeight: 700, color: e.dir === "IN" ? "var(--info)" : "var(--success)", minWidth: 68 }}>
                        {e.dir === "IN" ? "Received" : "Paid"}
                    </span>
                    <span style={{ fontWeight: 600, color: T.text0 }}>{e.type}</span>
                    <span style={{ color: T.text1, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.party}</span>
                    <span style={{ fontWeight: 700 }}>{money(e.amount)}</span>
                </div>
            ))}
        </Card>
    );
}